import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Save, RotateCcw, Plus, Trash2, Video } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
});

export const AdminVideos = () => {
  const [content, setContent] = useState({
    tagline: '',
    title: '',
    videos: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content/videos`);
      setContent(response.data);
    } catch (error) {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content/videos`, { ...content, id: 'videos' }, getAuthHeaders());
      toast.success('Videos saved!');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  const handleVideoChange = (index, field, value) => {
    const newVideos = [...content.videos];
    newVideos[index] = { ...newVideos[index], [field]: value };
    setContent(prev => ({ ...prev, videos: newVideos }));
  };

  const addVideo = () => {
    const newId = String(Date.now());
    setContent(prev => ({
      ...prev,
      videos: [...prev.videos, { id: newId, youtube_id: '', title: '', subtitle: '', extra_info: '' }]
    }));
  };

  const removeVideo = (index) => {
    setContent(prev => ({ ...prev, videos: prev.videos.filter((_, i) => i !== index) }));
  };

  // Extract YouTube ID from various URL formats
  const extractYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : url;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div data-testid="admin-videos">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-serif">Videos</h1>
          <p className="text-muted-foreground">Manage YouTube video embeds</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchContent} disabled={saving}>
            <RotateCcw className="w-4 h-4 mr-2" /> Reset
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-videos-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        <div className="bg-white rounded-lg border border-border p-6 space-y-4">
          <h2 className="font-semibold">Section Info</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Tagline</label>
              <input
                type="text"
                value={content.tagline}
                onChange={(e) => handleChange('tagline', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Title</label>
              <input
                type="text"
                value={content.title}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">Videos</h2>
            <Button variant="outline" size="sm" onClick={addVideo}>
              <Plus className="w-4 h-4 mr-1" /> Add Video
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {content.videos.map((video, index) => (
              <div key={video.id} className="border border-border rounded-lg p-4 relative">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => removeVideo(index)} 
                  className="absolute top-2 right-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                
                {video.youtube_id && (
                  <div className="mb-4 aspect-video bg-gray-100 rounded overflow-hidden">
                    <img 
                      src={`https://img.youtube.com/vi/${video.youtube_id}/hqdefault.jpg`} 
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium mb-1">YouTube URL or ID</label>
                    <input
                      type="text"
                      value={video.youtube_id}
                      onChange={(e) => handleVideoChange(index, 'youtube_id', extractYouTubeId(e.target.value))}
                      className="w-full px-3 py-2 text-sm border border-border rounded focus:border-primary focus:outline-none"
                      placeholder="e.g., dQw4w9WgXcQ or full YouTube URL"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Title</label>
                    <input
                      type="text"
                      value={video.title}
                      onChange={(e) => handleVideoChange(index, 'title', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-border rounded focus:border-primary focus:outline-none"
                      placeholder="e.g., Never Enough"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Subtitle</label>
                    <input
                      type="text"
                      value={video.subtitle}
                      onChange={(e) => handleVideoChange(index, 'subtitle', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-border rounded focus:border-primary focus:outline-none"
                      placeholder="e.g., The Greatest Showman"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Extra Info (optional)</label>
                    <input
                      type="text"
                      value={video.extra_info || ''}
                      onChange={(e) => handleVideoChange(index, 'extra_info', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-border rounded focus:border-primary focus:outline-none"
                      placeholder="e.g., West End Production"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminVideos;
